/**
 * Token Tools
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod";

import { getTokenSetExport } from "../api/token.js";
import { getCredentials } from "../common/credentials.js";
import { registerTrackedToolCall } from "../logging.js";
import { formatTokenSetsAsMarkdown } from "../common/formatters/token.js";
import { ServerContext } from "../types/server.js";

export function registerListTokenSetsTool(
  server: McpServer,
  context: ServerContext,
) {
  if (!context.features["TREE-2686_mcp_experimental_tools"]) {
    return;
  }

  registerTrackedToolCall(
    server,
    "list-token-sets",
    {
      title: "List token sets",
      description:
        "Call this BEFORE writing or modifying any CSS, SCSS, Tailwind, or inline styles that involve color, spacing, typography, radius, shadow, or motion. Lists the design token sets available in the connected styleguide so you can pick the right one to fetch with `get-tokens`. If multiple sets are available, prefer the set the user names; otherwise confirm which to use rather than guessing.",
      inputSchema: {},
      annotations: {
        title: "List token sets",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async () => {
      return {
        content: [
          {
            type: "text",
            text: formatTokenSetsAsMarkdown(context.tokenSets ?? []),
            mimeType: "text/markdown",
          },
        ],
      };
    },
  );
}

export function registerGetTokensTool(
  server: McpServer,
  context: ServerContext,
) {
  if (!context.features["TREE-2686_mcp_experimental_tools"]) {
    return;
  }

  registerTrackedToolCall(
    server,
    "get-tokens",
    {
      title: "Get tokens",
      description:
        "Fetch W3C design tokens from a token set. Call this BEFORE writing styles so colors, spacing, typography, radius, shadow, and motion values come from documented tokens instead of hardcoded literals. Use the exact token names from the response — do not invent, abbreviate, or approximate them. Use `list-token-sets` to find the token set ID. After generating styled code, run `lint-code` against the same `tokenSetId` to catch any hardcoded values that slipped through.",
      inputSchema: {
        tokenSetId: z.number().int().min(1).describe("The token set ID"),
      },
      annotations: {
        title: "Get tokens",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async ({ tokenSetId }, { authInfo }, clientUserAgent) => {
      const credentials = await getCredentials(authInfo);

      const tokens = await getTokenSetExport(
        credentials,
        tokenSetId,
        clientUserAgent,
      );

      if (tokens === null) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "Could not fetch tokens. Ensure the token set ID is valid and the token set has been published.",
            },
          ],
        };
      }

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(tokens, null, 2),
            mimeType: "application/json",
          },
        ],
      };
    },
  );
}
